import React, { useEffect, useState } from "react";
import YhInput from "../../../../../../ui/YhInput";
import YhSelect from "../../../../../../ui/YhSelect";
import YhButton from "../../../../../../ui/YhButton";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../../helpers/methods";
import { getLocationMeta } from "../ProjectLocationList/projectLocationService";
import { addZone, getZoneById } from "./ZoneService";
import notifiers from "../../../../../../global/constants/NotificationConstants";

type AddZoneProps = {
  closeDrawer: () => void;
  zoneId?: string | null;
  isEdit?: boolean;
};

const emptyZone = {
  countryId: "",
  stateId: "",
  cityId: "",
  zone_name: "",
  description: "",
  isTop: false,
};

const AddZone = ({ closeDrawer, zoneId, isEdit }: AddZoneProps) => {
  const [location, setLocation] = useState<any>({
    country: [],
    state: [],
    city: [],
  });
  const [zone, setZone] = useState<any>({ ...emptyZone });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getLocation();
  }, []);

  useEffect(() => {
    if (isEdit && zoneId) {
      fetchZoneById();
    } else {
      setZone({ ...emptyZone });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneId, isEdit]);

  const fetchZoneById = async () => {
    try {
      const res = await getZoneById(zoneId);
      if (res?.success === 1) {
        setZone({
          ...emptyZone,
          ...res.data,
          countryId: res.data?.countryId || "",
          stateId: res.data?.stateId || "",
          cityId: res.data?.cityId || "",
          zone_name: res.data?.zone_name || "",
          description: res.data?.description || "",
          isTop: !!res.data?.isTop,
        });
      }
    } catch (err) {
      console.error("Error fetching zone:", err);
    }
  };

  const getLocation = async () => {
    try {
      const locationRes = await getLocationMeta();
      const data = locationRes.data || [];

      const countryOptions: any[] = [];
      const stateOptions: any[] = [];
      const cityOptions: any[] = [];

      data.forEach((country: any) => {
        countryOptions.push({
          value: country._id,
          label: country.country_name,
        });

        country.states?.forEach((state: any) => {
          stateOptions.push({
            value: state._id,
            label: state.name,
            countryId: country._id,
          });

          state.cities?.forEach((city: any) => {
            cityOptions.push({
              value: city._id,
              label: city.name,
              stateId: state._id,
              countryId: country._id,
            });
          });
        });
      });

      setLocation({
        country: countryOptions,
        state: stateOptions,
        city: cityOptions,
      });
    } catch (error: any) {
      openErrorNotification(error.message || notifiers.GENERIC_ERROR);
    }
  };

  const filteredStates =
    location?.state?.filter((s: any) => s.countryId === zone.countryId) || [];
  const filteredCities =
    location?.city?.filter((c: any) => c.stateId === zone.stateId) || [];

  const handleSubmit = async () => {
    try {
      if (!zone.countryId) return openErrorNotification("Country is required");
      if (!zone.stateId) return openErrorNotification("State is required");
      if (!zone.cityId) return openErrorNotification("City is required");
      if (!zone.zone_name?.trim())
        return openErrorNotification("Zone name is required");

      setSubmitting(true);
      const res = await addZone({
        ...zone,
        ...(isEdit && zoneId ? { _id: zoneId } : {}),
        zone_name: zone.zone_name.trim(),
      });
      if (res.success === 1) {
        openSuccessNotification(
          isEdit ? "Zone updated successfully" : "Zone added successfully"
        );
        closeDrawer();
      } else {
        openErrorNotification(res.message || "Failed to save zone");
      }
    } catch (err: any) {
      openErrorNotification(err?.message || notifiers.GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4"
      onClick={closeDrawer}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#EEF0F5] px-5 py-4">
          <h2 className="text-lg font-bold text-[#111827]">
            {isEdit ? "Edit Zone" : "Create Zone"}
          </h2>
          <button
            type="button"
            onClick={closeDrawer}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <YhSelect
              label="Country"
              requiredMark
              placeholder="Select Country"
              value={zone.countryId}
              options={location.country || []}
              onChange={(e) =>
                setZone({
                  ...zone,
                  countryId: e.target.value,
                  stateId: "",
                  cityId: "",
                })
              }
            />

            <YhSelect
              label="State"
              requiredMark
              placeholder="Select State"
              value={zone.stateId}
              options={filteredStates}
              disabled={!zone.countryId}
              onChange={(e) =>
                setZone({
                  ...zone,
                  stateId: e.target.value,
                  cityId: "",
                })
              }
            />

            <YhSelect
              label="City"
              requiredMark
              placeholder="Select City"
              value={zone.cityId}
              options={filteredCities}
              disabled={!zone.stateId}
              onChange={(e) =>
                setZone({
                  ...zone,
                  cityId: e.target.value,
                })
              }
            />

            <YhInput
              label="Zone Name"
              requiredMark
              placeholder="e.g. Golf Course Road"
              value={zone.zone_name}
              onChange={(e) =>
                setZone({ ...zone, zone_name: e.target.value })
              }
            />

            <div className="sm:col-span-2">
              <YhInput
                label="Description"
                placeholder="Optional description"
                value={zone.description || ""}
                onChange={(e) =>
                  setZone({ ...zone, description: e.target.value })
                }
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-[#374151] sm:col-span-2">
              <input
                type="checkbox"
                checked={!!zone.isTop}
                onChange={(e) =>
                  setZone({ ...zone, isTop: e.target.checked })
                }
              />
              Mark as popular / top zone
            </label>
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-[#EEF0F5] bg-[#FAFBFC] px-5 py-4">
          <YhButton variant="secondary" size="sm" onClick={closeDrawer}>
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving…" : isEdit ? "Update Zone" : "Create Zone"}
          </YhButton>
        </div>
      </div>
    </div>
  );
};

export default AddZone;
